import React, { useEffect, useState, useCallback, useMemo, memo } from 'react';
import axios from 'axios';
import ToggleButton from 'react-bootstrap/ToggleButton';
import UserTopbar from '../component/UserTopbar'
import { BiBarcodeReader } from "react-icons/bi";
import { BiCheckCircle, BiXCircle } from "react-icons/bi";
import ProgressBar from 'react-bootstrap/ProgressBar';
import { BiCalendar } from "react-icons/bi";
import { FcOk, FcHighPriority, FcFinePrint, FcLeave } from "react-icons/fc";
import { useParams } from "react-router-dom";
import Swal from 'sweetalert2';
import moment from 'moment';
import { callMsGraph } from '../callMsGroup';
import LoadingScreen from '../component/LoadingScreen';
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { acquireGetAccessToken } from '../utils/GetGrapApi'
import { Buffer } from 'buffer';
import { BiLike, BiDislike } from "react-icons/bi";


// Interfaces for better type safety
interface UserProfile {
    name: string;
    employeeId: string;
    usR_PERSONID: string;
    date: string;
}

interface Employee {
    id: number;
    eventId: number;
    employeeId: string;
    recieved: string;
    takeRecieved: string;
    updatedBy: string;
    updatedAt: string;
    reward: any[];
    employee: {
        employeeId: string;
        namempt: string;
        nampost: string;
        codcom2: string;
        [key: string]: any;
    };
}

interface EventData {
    id: number;
    eventNumber: string;
    eventName: string;
    description: string;
    startDate: string;
    endDate: string;
    confirmStatus: string;
    behalStatus: string;
    totalRecieved: number;
    totalMember: number;
    owner: string;
    eventRewards: any[];
}

function Event() {
    let { eventnumber }: any = useParams();
    
    // Combined related states
    const [appState, setAppState] = useState({
        loading: false,
        loadingPage: false,
        success: false,
        duplicate: false,
        stateLoad: false,
        onBehalfCheck: false,
        onBehalf: true,
        confirmEvent: true
    });

    // Cache status state
    const [cacheStatus, setCacheStatus] = useState<{ status: string, itemCount: number, cacheDate: string } | null>(null);

    // Form states
    const [empId, setEmpId] = useState("");
    const [tempEmpId, setTempEmpId] = useState("");
    
    // Auth states
    const [authState, setAuthState] = useState({
        token: "",
        tokenProfile: "",
        photoData: "",
        profile: { jobTitle: "", mail: "" }
    });

    // Data states
    const [userProfileR, setProfileR] = useState<UserProfile>({
        name: "",
        employeeId: "",
        usR_PERSONID: "",
        date: ""
    });

    const [employee, setEmployee] = useState<Employee>({
        id: 0,
        eventId: 0,
        employeeId: "",
        recieved: "",
        takeRecieved: "",
        updatedBy: "",
        updatedAt: "",
        reward: [],
        employee: {
            employeeId: "",
            namempt: "",
            nampost: "",
            codcom2: ""
        }
    });

    const [eventdata, setEventdata] = useState<EventData>({
        id: 0,
        eventNumber: "",
        eventName: "",
        description: "",
        startDate: "",
        endDate: "",
        confirmStatus: "",
        behalStatus: "",
        totalRecieved: 0,
        totalMember: 0,
        owner: "",
        eventRewards: []
    });

    const { inProgress, instance, accounts } = useMsal();

    // Memoized auth headers
    const auths = useMemo(() => ({
        headers: {
            "Content-type": "application/json",
            'Authorization': 'Bearer ' + (sessionStorage.getItem("accessToken") || authState.token)
        }
    }), [authState.token]);

    // Memoized date conversion functions
    const convertToG7 = useCallback((date: any) => {
        if (!date) return '';
        let formattedDate;
        
        if (typeof date === 'string' && date.includes('Z')) {
            formattedDate = moment(date);
        } else {
            formattedDate = moment.utc(date);
        }
        return formattedDate.utcOffset('+07:00').format('DD-MM-YYYY HH:mm:ss');
    }, []);

    const convertToG72 = useCallback((date: any) => {
        if (!date) return '';
        return moment(date).format('DD-MMM-YYYY');
    }, []);

    const convertToCommaSeparated = useCallback((number: number) => {
        return number.toLocaleString();
    }, []);

    // Memoized progress calculation
    const progressPercentage = useMemo(() => {
        if (!eventdata.totalMember) return 0;
        return Math.round((eventdata.totalRecieved / eventdata.totalMember) * 100);
    }, [eventdata.totalRecieved, eventdata.totalMember]);

    // Optimized event handlers with useCallback
    const getUserInfo = useCallback(async (employeeId: any) => {
        try {
            const result = await axios.get(
                `${process.env.REACT_APP_API_END_POINT}/v1/Member/getUserInfo?EmployeeID=${employeeId}`,
                auths
            );
            return result.data;
        } catch (error: any) {
            console.error('Error fetching user info:', error);
            
            // Handle 401 Unauthorized
            if (error.response?.status === 401) {
                console.error('Token expired or unauthorized, attempting to refresh...');
                // Try to refresh token
                try {
                    const newTokens = await acquireGetAccessToken(instance, accounts);
                    if (newTokens && newTokens.accessToken) {
                        sessionStorage.setItem("accessToken", newTokens.accessToken);
                        
                        // Retry with new token
                        const retryResult = await axios.get(
                            `${process.env.REACT_APP_API_END_POINT}/v1/Member/getUserInfo?EmployeeID=${employeeId}`,
                            {
                                headers: {
                                    "Content-type": "application/json",
                                    'Authorization': 'Bearer ' + newTokens.accessToken
                                }
                            }
                        );
                        return retryResult.data;
                    }
                } catch (refreshError) {
                    console.error('Failed to refresh token:', refreshError);
                    return null;
                }
            }
            
            return null;
        }
    }, [auths, instance, accounts]);

    // Load total members count
    const loadTotalMembers = useCallback(async (eventId: number) => {
        if (!eventId) return;
        
        try {
            const url = `${process.env.REACT_APP_API_END_POINT}/v1/member/Members?eventId=${eventId}&offset=0&limit=999999`;
            const response = await axios.get(url, auths);
            
            const total = response.data.total || 0;
            const received = response.data.memberLists?.filter((m: any) => m.recieved === 1 || m.recieved === '1').length || 0;
            
            // Update eventdata with correct totals
            setEventdata(prev => ({
                ...prev,
                totalMember: total,
                totalRecieved: received
            }));
        } catch (error) {
            console.error('Error loading total members:', error);
        }
    }, [auths]);

    const onRecieved = useCallback(async (takeRecieved: any) => {
        setAppState(prev => ({ ...prev, loading: true }));
        setTempEmpId(empId);
        
        const params = takeRecieved ? {
            eventNumber: eventnumber,
            employeeId: empId,
            takeRecieved: takeRecieved,
        } : {
            eventNumber: eventnumber,
            employeeId: empId
        };
        
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_END_POINT}/v1/Events/recieved`,
                JSON.stringify(params),
                auths
            );
            
            setEmployee(response.data);
            setProfileR(response.data.takeRecievedData ? {
                name: response.data.takeRecievedData.namempt,
                employeeId: response.data.takeRecievedData.employeeId,
                usR_PERSONID: response.data.takeRecievedData.usR_PERSONID,
                date: response.data.takeRecievedData.updatedAt
            } : { name: "", employeeId: "", usR_PERSONID: "", date: "" });
            setEmpId("");
            setAppState(prev => ({ ...prev, loading: false, duplicate: false }));
            
            // Reload total members count after successful receipt
            if (eventdata.id) {
                loadTotalMembers(eventdata.id);
            }
        } catch (error) {
            console.error('Error recording receipt:', error);
            setEmpId("");
            setAppState(prev => ({ ...prev, loading: false, duplicate: false }));
        }
    }, [empId, eventnumber, auths, eventdata.id, loadTotalMembers]);

    const onValidate = useCallback(async (takeRecieved: any) => {
        setAppState(prev => ({ ...prev, loading: true }));
        setTempEmpId(empId);
        
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_END_POINT}/v1/Events/validateEmployee?eventId=${eventdata.id}&employeeId=${empId}`,
                auths
            );
            
            // Check for error message in response
            if (response.data?.Message || response.data?.message) {
                const errorMsg = response.data.Message || response.data.message;
                Swal.fire({
                    title: 'ข้อผิดพลาด',
                    text: errorMsg,
                    icon: 'error',
                    confirmButtonText: 'ตกลง'
                });
                setEmpId("");
                setAppState(prev => ({ ...prev, loading: false, duplicate: false }));
                return;
            }
            
            if (response.data.length === 0) {
                await onRecieved(takeRecieved);
            } else {
                if (response.data.takeRecievedData) {
                    setProfileR({
                        name: response.data.takeRecievedData.namempt,
                        employeeId: response.data.takeRecievedData.employeeId,
                        usR_PERSONID: response.data.takeRecievedData.usR_PERSONID,
                        date: response.data.takeRecievedData.updatedAt,
                    });
                } else {
                    setProfileR({ name: "", employeeId: "", usR_PERSONID: "", date: "" });
                }
                setEmployee(response.data);
                setEmpId("");
                setAppState(prev => ({ ...prev, loading: false, duplicate: true }));
            }
        } catch (error: any) {
            console.error('Error validating employee:', error);
            
            // Check for API error message
            let errorMessage = 'เกิดข้อผิดพลาดในการตรวจสอบข้อมูล';
            
            if (error.response?.data?.detail) {
                errorMessage = error.response.data.detail.ErrorTh || error.response.data.detail.Message || errorMessage;
            } else if (error.response?.data?.Message) {
                errorMessage = error.response.data.Message;
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            }
            
            Swal.fire({
                title: 'ข้อผิดพลาด',
                text: errorMessage,
                icon: 'error',
                confirmButtonText: 'ตกลง'
            });
            
            setEmpId("");
            setAppState(prev => ({ ...prev, loading: false, duplicate: false }));
        }
    }, [empId, eventdata.id, auths, onRecieved]);

    const handleModalonBehalf = useCallback(async (userInfo: any) => {
        // Validate userInfo before proceeding
        if (!userInfo || !userInfo.employeeId) {
            Swal.fire({
                title: 'ข้อผิดพลาด',
                text: 'ไม่พบรหัสพนักงานในระบบ กรุณาตรวจสอบรหัสพนักงานก่อนเลือกรับแทน',
                icon: 'error',
                confirmButtonText: 'ตกลง'
            });
            setEmpId("");
            setAppState(prev => ({ ...prev, onBehalfCheck: false }));
            return;
        }

        const ownerName = userInfo.namempt || 'ไม่ระบุชื่อ';
        const ownerEmpId = userInfo.usR_PERSONID;
        
        try {
            const result = await Swal.fire({
                title: 'รับของแทน',
                html: `
                    <div style="text-align: left; padding: 10px;">
                        <p><strong>พนักงานเจ้าของของ:</strong></p>
                        <p>รหัส: ${ownerEmpId}</p>
                        <p>ชื่อ: ${ownerName}</p>
                        <hr style="margin: 15px 0;">
                        <p><strong>กรุณาป้อนรหัสพนักงานคนที่มารับแทน:</strong></p>
                    </div>
                `,
                input: 'number',
                icon: 'info',
                inputPlaceholder: 'ป้อนรหัสพนักงานคนมารับแทน',
                showCancelButton: true,
                confirmButtonText: 'ยืนยัน',
                cancelButtonText: 'ยกเลิก',
                allowOutsideClick: false,
                allowEscapeKey: false,
                allowEnterKey: false,
                preConfirm: async (value) => {
                    if (!value || isNaN(value)) {
                        Swal.showValidationMessage('กรุณาป้อนรหัสพนักงานของคนที่มารับแทน');
                        return false;
                    }
                    
                    if (value === ownerEmpId) {
                        Swal.showValidationMessage('ไม่สามารถใช้รหัสพนักงานเดียวกันได้');
                        return false;
                    }
                
                    const userProfileInfo = await getUserInfo(value);           
                    if (!userProfileInfo || !userProfileInfo.employeeId) {
                        Swal.showValidationMessage('ไม่พบรหัสพนักงานที่รับแทนในระบบ');
                        return false;
                    }
                    return value;
                }
            });

            if (result.isConfirmed) {
                await onValidate(result.value);
                setAppState(prev => ({ ...prev, onBehalfCheck: false }));
            } else if (result.dismiss === Swal.DismissReason.cancel) {
                setEmpId("");
                setAppState(prev => ({ ...prev, onBehalfCheck: false }));
            }
        } catch (error) {
            console.error('Error in behalf modal:', error);
            setEmpId("");
            setAppState(prev => ({ ...prev, onBehalfCheck: false }));
        }
    }, [getUserInfo, onValidate]);
    
    const handleModalConfirm = useCallback(async (userInfo: any) => {
        const hasValidUserInfo = userInfo && userInfo.employeeId;
        
        if (!hasValidUserInfo) {
            Swal.fire({
                title: 'ไม่พบข้อมูลพนักงาน',
                text: 'ไม่พบรหัสพนักงานในระบบ กรุณาตรวจสอบรหัสพนักงานอีกครั้ง',
                icon: 'error',
                confirmButtonText: 'ตกลง'
            });
            setEmpId("");
            return;
        }
        
        try {
            const result = await Swal.fire({
                title: 'ยืนยันการรับของ',
                html: `
                    <div style="text-align: left; padding: 10px;">
                        <p><strong>รหัสพนักงาน:</strong> ${userInfo.usR_PERSONID}</p>
                        <p><strong>ชื่อ:</strong> ${userInfo.namempt || 'ไม่ระบุ'}</p>
                        <p><strong>แผนก:</strong> ${userInfo.namcenteng || 'ไม่ระบุ'}</p>
                        <hr style="margin: 15px 0;">
                        <p>กรุณายืนยันการรับของ</p>
                    </div>
                `,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'ยืนยันรับของ',
                cancelButtonText: 'ยกเลิก',
                allowOutsideClick: false,
                allowEscapeKey: false,
                allowEnterKey: false
            });

            if (result.isConfirmed) {
                await onValidate(null);
            } else if (result.dismiss === Swal.DismissReason.cancel) {
                setEmpId("");
            }
        } catch (error) {
            console.error('Error in confirm modal:', error);
            setEmpId("");
        }
    }, [onValidate]);

    const hendelChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value; 
        setEmpId(value);
    }, []);

    const hendleEnter = useCallback(async (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.keyCode === 13) {
            // Don't proceed if employee ID is empty
            if (!empId.trim()) {
                return;
            }

            setProfileR({ name: "", employeeId: "", usR_PERSONID: "", date: "" });

            // Optimize: Only fetch user info when actually needed
            const shouldFetchUserInfo = (appState.confirmEvent && !appState.onBehalfCheck && empId.length === 7) || appState.onBehalfCheck ;
            // console.log('shouldFetchUserInfo:', shouldFetchUserInfo, {
            //     confirmEvent: appState.confirmEvent,
            //     onBehalfCheck: appState.onBehalfCheck,
            //     empIdLength: empId.length,
            //     empId: empId
            // });
            
            if (!shouldFetchUserInfo) {
                // Direct validation - fastest path
                onValidate(null);
                return;
            }
            
            // Fetch user info only when needed
            const userInfo = await getUserInfo(empId);
            
            if (appState.onBehalfCheck) {
                // Handle behalf mode
                if (!userInfo) {
                    Swal.fire({
                        title: 'ข้อผิดพลาด',
                        text: 'ไม่พบรหัสพนักงานในระบบ กรุณาตรวจสอบอีกครั้ง',
                        icon: 'error',
                        confirmButtonText: 'ตกลง'
                    });
                    return;
                }
                handleModalonBehalf(userInfo);
            } else {
                // Show confirmation modal for short employee IDs
                handleModalConfirm(userInfo);
            }
        }
    }, [empId, appState.confirmEvent, appState.onBehalfCheck, getUserInfo, handleModalConfirm, handleModalonBehalf, onValidate]);

    // Check cache status
    const checkCacheStatus = useCallback(async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_END_POINT}/Cache/EmployeesCacheStatus`,
                auths
            );
            // response.data = { status: "active", itemCount: 11212, cacheDate: "2025-11-10 23:10:58" }
            setCacheStatus(response.data);
        } catch (error) {
            console.error('Error checking cache status:', error);
            setCacheStatus({ status: "inactive", itemCount: 0, cacheDate: "" });
        }
    }, [auths]);

    // Optimized data loading with useCallback
    const loadData = useCallback(async () => {
        setAppState(prev => ({ ...prev, loadingPage: true }));
        
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_END_POINT}/v1/events/eventactive?eventNumber=${eventnumber}`,
                auths
            );
            
            if (response.data && response.data.id) {
                setEventdata(response.data);
                setAppState(prev => ({ 
                    ...prev, 
                    confirmEvent: response.data.confirmStatus === '1',
                    onBehalf: response.data.behalStatus === '1',
                    success: true,
                    loadingPage: false,
                    stateLoad: true
                }));
                
                // Load actual member counts
                loadTotalMembers(response.data.id);
            } else {
                // Event not found or not active
                setAppState(prev => ({ 
                    ...prev, 
                    success: false,
                    loadingPage: false,
                    stateLoad: true
                }));
            }
        } catch (error: any) {
            console.error('Error loading event data:', error);
            
            // Set state first to stop loading
            setAppState(prev => ({ 
                ...prev, 
                success: false, 
                loadingPage: false,
                stateLoad: true
            }));
            
            // Check if it's an API error response
            let errorMessage = 'ขอโทษ กิจกรรมนี้ยังไม่เปิดใช้งาน หรือไม่พบข้อมูลที่คุณกำลังค้นหา';
            
            if (error.response?.data?.detail) {
                // Use the Thai error message from API if available
                errorMessage = error.response.data.detail.ErrorTh || error.response.data.detail.Message || errorMessage;
            }
            
            // Show error message to user after state is updated
            Swal.fire({
                title: 'ไม่พบข้อมูลกิจกรรม',
                text: errorMessage,
                icon: 'error',
                confirmButtonText: 'ตกลง'
            });
        }
    }, [eventnumber, auths, loadTotalMembers]);

    // Auth token acquisition effect
    useEffect(() => {
        if (!authState.token && inProgress === InteractionStatus.None) {
            acquireGetAccessToken(instance, accounts).then(async (res: any) => {
                try {
                    // Get photo
                    const photoResponse = await axios("https://graph.microsoft.com/v1.0/me/photo/$value", {
                        headers: { Authorization: `Bearer ${res.accessTokenProfile}` },
                        responseType: "arraybuffer"
                    });
                    const avatar = Buffer.from(photoResponse.data, "binary").toString("base64");
                    const photoData = "data:image/jpeg;base64, " + avatar;

                    // Get profile
                    const profileResponse = await axios("https://graph.microsoft.com/v1.0/me", {
                        headers: { Authorization: `Bearer ${res.accessTokenProfile}` }
                    });

                    setAuthState({
                        token: res.accessToken,
                        tokenProfile: res.accessTokenProfile,
                        photoData: photoData,
                        profile: profileResponse.data
                    });
                } catch (error) {
                    console.error('Error fetching auth data:', error);
                    setAuthState(prev => ({
                        ...prev,
                        token: res.accessToken,
                        tokenProfile: res.accessTokenProfile
                    }));
                }
            });
        }
    }, [inProgress, authState.token, instance, accounts]);

    // Role checking effect with optimization
    useEffect(() => {
        const checkRole = async () => {
            if (eventdata.owner && authState.profile.mail && appState.success) {
                try {
                    const ownerArray = eventdata.owner.split(";").map(em => em.trim());
                    const profileMailLower = authState.profile.mail.toLowerCase().trim();
                    const ownerExists = ownerArray.some((em) => em.toLowerCase() === profileMailLower);
                    
                    const groupsData = await callMsGraph(
                        authState.tokenProfile, 
                        "/me/transitiveMemberOf/microsoft.graph.group?$top=999"
                    );
                    const groupExists = groupsData.value.some((group: any) => 
                        group.id === process.env.REACT_APP_SEC_ADMIN
                    );

                    if (groupExists || ownerExists) {
                        setAppState(prev => ({ ...prev, stateLoad: true }));
                    } else {
                        window.location.href = `${window.location.origin}/accessdenied`;
                    }
                } catch (error) {
                    console.error('Error checking role:', error);
                }
            }
        };

        checkRole();
    }, [eventdata.owner, authState.profile.mail, authState.tokenProfile, appState.success]);

    // Load data when token is available
    useEffect(() => {
        if (authState.token) {
            loadData();
            checkCacheStatus();
        }
    }, [authState.token, loadData, checkCacheStatus]);

    // Periodically check cache status every 30 seconds
    useEffect(() => {
        if (authState.token) {
            const interval = setInterval(() => {
                checkCacheStatus();
            }, 30000); // Check every 30 seconds

            return () => clearInterval(interval);
        }
    }, [authState.token, checkCacheStatus]);

    // Early return for loading state
    if (appState.loadingPage) {
        return <LoadingScreen />;
    }

    return (
        <>
        {
            (eventdata.eventNumber !== "" && appState.stateLoad) ? <>
                <UserTopbar token={authState.token} photoData={authState.photoData} />
                <div className='container-fluid event-bg p-0'>
                    <div className='container pt-4'>
                        {
                            (eventdata.eventNumber !== "" && appState.stateLoad) ?
                                <div className='row row-form'>
                                    <div className='col-6'>
                                        <div className='event-info p-5'>
                                            <ProgressBar 
                                                variant="success" 
                                                now={progressPercentage} 
                                                label={`${progressPercentage}%`} 
                                            />
                                            <div className='w-100 text-end mt-1'>
                                                จำนวน {convertToCommaSeparated(eventdata.totalRecieved)} / {convertToCommaSeparated(eventdata.totalMember)} คน 
                                            </div>
                                            <br />
                                            <h1>{eventdata.eventName}</h1>
                                            <div className='date'>
                                                <BiCalendar size={22} />
                                                <span className='me-1'>
                                                    {convertToG72(eventdata.startDate)}
                                                </span>
                                                -
                                                <span className='ms-1'>
                                                    {convertToG72(eventdata.endDate)}
                                                </span>
                                            </div>
                                            <p className='mt-3'>{eventdata.description}</p>
                                            <div className='mt-4 mb-2'>
                                                รายการของแจก
                                            </div>
                                            <table className="table items-table-user mt-4">
                                                <tbody>
                                                    {
                                                        (eventdata.eventRewards.length > 0) ?
                                                            eventdata.eventRewards.map(
                                                                (item: any,
                                                                    index: number) => (
                                                                    <tr key={index}>
                                                                        <td>
                                                                            <img src={`data:image/png;base64,${item?.image}`} alt={item?.rewardName || 'reward'} />
                                                                        </td>
                                                                        <td>{item?.rewardName} <p>จำนวน : {item?.qty} ชิ้น</p></td>
                                                                    </tr>
                                                                )
                                                            )
                                                            :
                                                            <></>
                                                    }
                                                </tbody>
                                            </table>

                                        </div>
                                    </div>
                                    <div className='col-6 p-0'>

                                        <div className='register-form'>
                                            <div className='d-flex justify-content-between align-items-center mb-2'>
                                                <div>
                                                    <label className='mb-0'>กรุณากรอกรหัสพนักงาน</label> <span className='text-danger'> (7 หลัก)</span>
                                                </div>
                                                <div className='text-end'>
                                                    {cacheStatus === null ? (
                                                        <small className='text-muted'>กำลังตรวจสอบ...</small>
                                                    ) : cacheStatus.status === "active" ? (
                                                        <span>
                                                            <BiLike size={24} className='text-success' title='Cache Active' />
                                                            {/* <span className='ms-2 text-success' style={{ fontSize: '13px' }}>
                                                                {cacheStatus.itemCount} รายการ
                                                            </span>
                                                            <span className='ms-2 text-secondary' style={{ fontSize: '12px' }}>
                                                                {cacheStatus.cacheDate}
                                                            </span> */}
                                                        </span>
                                                    ) : (
                                                        <span>
                                                            <BiDislike size={24} className='text-danger' title='Cache Inactive' />
                                                            {/* <span className='ms-2 text-danger' style={{ fontSize: '13px' }}>
                                                                ไม่มี Cache
                                                            </span> */}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="input-group">
                                                <input 
                                                    type="text" 
                                                    className="form-control" 
                                                    aria-describedby="basic-addon2" 
                                                    onChange={hendelChange} 
                                                    onKeyDown={hendleEnter}
                                                    value={empId} 
                                                    autoFocus={true}
                                                    pattern="[0-9]*"
                                                    inputMode="numeric"
                                                    maxLength={10}
                                                    placeholder="กรุณากรอกตัวเลขเท่านั้น"
                                                    onBeforeInput={(e: React.FormEvent<HTMLInputElement>) => {
                                                        const input = (e.nativeEvent as InputEvent).data;
                                                        if (input && !/^\d+$/.test(input)) {
                                                            e.preventDefault();
                                                            
                                                            if (/[\u0E00-\u0E7F]/.test(input)) {
                                                                Swal.fire({
                                                                    title: 'แจ้งเตือน',
                                                                    text: 'กรุณากรอกเฉพาะตัวเลขเท่านั้น ไม่สามารถใช้ภาษาไทยได้',
                                                                    icon: 'warning',
                                                                    showConfirmButton: false,
                                                                    timer: 1500,
                                                                    timerProgressBar: true,
                                                                    position: 'top-end',
                                                                    toast: true
                                                                });
                                                            }
                                                        }
                                                    }}
                                                    onPaste={(e) => {
                                                        // ป้องกันการวางข้อมูลที่ไม่ใช่ตัวเลข
                                                        e.preventDefault();
                                                        const pastedData = e.clipboardData.getData('text');
                                                        if (/^\d+$/.test(pastedData)) {
                                                            const newValue = pastedData.slice(0, 10); // จำกัดความยาวไม่เกิน 10 ตัว
                                                            setEmpId(newValue);
                                                        }
                                                    }}
                                                />
                                                { appState.onBehalf ? ( 
                                                <ToggleButton
                                                    id="toggle-check"
                                                    type="checkbox"
                                                    variant="outline-primary"
                                                    checked={appState.onBehalfCheck}
                                                    value="1"
                                                    onChange={(e) => {
                                                        const isChecked = e?.currentTarget?.checked ?? false;
                                                        setAppState(prev => ({ 
                                                            ...prev, 
                                                            onBehalfCheck: isChecked 
                                                        }));
                                                    }}
                                                >
                                                    รับแทน
                                                </ToggleButton>
                                                ) : null}
                                                <span className="input-group-text" id="basic-addon2" onClick={() => onValidate(null)}>
                                                    <BiBarcodeReader size={26} />
                                                </span>
                                            </div>
                                        </div>


                                        <div className='result-form text-center p-5 pt-4 position-relative'>
                                            {
                                                (tempEmpId === "") ?
                                                    <>
                                                        <FcFinePrint size={96} />
                                                        <h2 className='mt-3 text-secondary'>ยินดีต้อนรับเข้าสู่ระบบ</h2>
                                                    </>
                                                    : <>
                                                        {
                                                            (!appState.loading) ?
                                                                (employee.employeeId !== "" && employee.recieved === "1") ?
                                                                    <>
                                                                        {
                                                                            (appState.duplicate) ?
                                                                                <>
                                                                                    <FcHighPriority className='text-warning' size={96} />
                                                                                    <h2 className='mt-3 text-warning'>เคยลงทะเบียนแล้ว</h2>
                                                                                    <p>
                                                                                        กรุณาตรวจสอบข้อมูลรหัสพนักงานอีกครั้ง
                                                                                    </p>
                                                                                </>
                                                                                : <>
                                                                                    <FcOk size={72} />
                                                                                    <h4 className='mt-3'>บันทึกข้อมูลเรียบร้อย</h4>
                                                                                    <p>
                                                                                        ระบบทำการบันทึกข้อมูลรายการเรียบร้อยแล้ว
                                                                                    </p>
                                                                                </>
                                                                        }

                                                                        <div className='row mt-4 border-top '>
                                                                            <div className='col-6  text-start headline'>
                                                                                รหัสพนักงาน
                                                                            </div>
                                                                            <div className='col-6  text-start headline'>
                                                                                ชื่อ-นามสกุล
                                                                            </div>
                                                                            <div className='col-6 mb-3 text-start '>
                                                                                {employee.employeeId}
                                                                            </div>
                                                                            <div className='col-6 mb-3 text-start'>
                                                                                {employee.employee.namempt}
                                                                            </div>
                                                                            <div className='col-6  text-start headline'>
                                                                                ตำแหน่ง
                                                                            </div>
                                                                            <div className='col-6  text-start headline'>
                                                                                ตึก
                                                                            </div>
                                                                            <div className='col-6 mb-3 text-start'>
                                                                                {employee.employee.nampost}
                                                                            </div>
                                                                            <div className='col-6 mb-3 text-start'>
                                                                                {employee.employee.codcom2}
                                                                            </div>
                                                                            {(!userProfileR?.employeeId?.length) ? 
                                                                            <>
                                                                            <div className='col-6  text-start headline'>
                                                                                วันที่รับ
                                                                            </div>
                                                                            <div className='col-6  text-start headline'>
                                                                            
                                                                            </div>
                                                                            <div className='col-6 mb-3 text-start'>
                                                                                {convertToG7(employee.updatedAt)} น.
                                                                            </div>
                                                                            <div className='col-6 mb-3 text-start'>
                                                                                
                                                                            </div>
                                                                            </>:<></>
                                                                            }
                                                                
                                                                            {
                                                                                (userProfileR?.employeeId?.length > 0 ) ?  
                                                                                    <div className='col-12'>
                                                                                        <div className='mt-4 mb-2 text-start fw-bold'>
                                                                                            คนที่มารับแทน
                                                                                        </div>
                                                                                        <div className='col-12 mb-3 text-start text-danger'>
                                                                                            {userProfileR?.name} (รหัสพนักงาน :  {userProfileR?.usR_PERSONID})
                                                                                        </div>
                                                                                        <div className='mt-4 mb-2 text-start fw-bold'>
                                                                                            วันที่รับแทน
                                                                                        </div>
                                                                                        <div className='col-12 mb-3 text-start text-danger'>
                                                                                            {convertToG7(employee.updatedAt)} น.
                                                                                        </div>
                                                                                    </div>
                                                                                : <></>
                                                                            }
                                                                            {
                                                                                (employee.reward.length > 0) ?
                                                                                    <div className='col-12'>
                                                                                        <div className='mt-4 mb-2 text-start fw-bold'>
                                                                                            รายการของที่ได้รับ
                                                                                        </div>
                                                                                        <table className="table items-table-user mt-4">
                                                                                            <tbody>
                                                                                                {
                                                                                                    employee.reward.map(
                                                                                                        (item: any, index: number) => (
                                                                                                            <tr key={index}>
                                                                                                                <td>
                                                                                                                    <img src={`data:image/png;base64,${item?.image}`} alt="reward" />
                                                                                                                </td>
                                                                                                                <td>{item?.rewardName} <p>จำนวน : {item?.qty} ชิ้น</p></td>
                                                                                                            </tr>
                                                                                                        )
                                                                                                    )
                                                                                                }
                                                                                            </tbody>
                                                                                        </table>
                                                                                    </div>
                                                                                    : <></>
                                                                            }

                                                                        </div>
                                                                    </>
                                                                    : <>
                                                                        <FcHighPriority size={96} />
                                                                        <h2 className='mt-3 text-danger'>ไม่พบข้อมูลรหัสพนักงาน {tempEmpId}</h2>
                                                                        <p>
                                                                            กรุณาตรวจสอบข้อมูลรหัสพนักงานอีกครั้ง
                                                                        </p>
                                                                    </>
                                                                :
                                                                <div className='findEmp'>
                                                                    <div className='dots'></div>
                                                                    <label>..:: กำลังตรวจสอบข้อมูลพนักงาน กรุณารอสักครู่ ::..</label>
                                                                </div>
                                                        }
                                                    </>
                                            }
                                        </div>
                                    </div>
                                </div>
                                :
                                <div className='ev_not_found text-center'>
                                    {
                                        (appState.stateLoad && !appState.success) ?
                                            <>
                                                <FcLeave size={96} />
                                                <p>ขอโทษ กิจกรรมนี้ยังไม่เปิดใช้งาน หรือไม่พบข้อมูลที่คุณกำลังค้นหา</p>
                                            </>
                                            : <></>
                                    }

                                </div>
                        }
                    </div>
                </div >
            </>:<><LoadingScreen /></>
            }
        </>

    )
}

export default memo(Event);

